import { Globe } from "lucide-react";
import { Eyebrow } from "@/components/PageHero";
import { QuoteButton } from "@/components/QuoteButton";
import { useContent, useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";

const PRESTATIONS = [
  "Coordination du jour J uniquement",
  "Préparation et coordination du jour J",
  "Accompagnement personnalisé selon le projet",
  "Coordination de prestataires",
  "Suivi du planning et des étapes clés",
  "Coordination logistique et opérationnelle",
  "Missions ponctuelles auprès d'agences, d'entreprises, d'organisateurs et de producteurs",
];

export default function Coordination() {
  useReveal();
  const { content } = useContent();
  const img = useSiteImage();
  const zone = content?.zones?.coordination;

  return (
    <div data-testid="coordination-page">
      <Seo
        title="Coordination événementielle & coordination du jour J — Afro Regal"
        description="Coordination événementielle indépendante : préparation, coordination du jour J, gestion des prestataires et suivi opérationnel. Disponible partout en France et à l'international."
      />
      <section className="relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-40" data-testid="coordination-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="relative order-2 h-[440px] sm:h-[520px] lg:order-1 lg:col-span-6">
            <div className="absolute left-0 top-0 h-[78%] w-[72%] overflow-hidden rounded-2xl shadow-xl">
              <img src={img("coordination_hero")} alt="Réception coordonnée par Afro Regal" className="h-full w-full object-cover" style={{ objectPosition: "15% center" }} data-testid="coordination-hero-image" />
            </div>
            <div className="absolute bottom-0 right-0 h-[58%] w-[52%] overflow-hidden rounded-2xl border-[6px] border-[#FAF6EF] shadow-2xl">
              <img src={img("ambiance_1")} alt="Détails d'une réception" className="h-full w-full object-cover" />
            </div>
            <div className="absolute bottom-8 left-4 rounded-full bg-[#2C2C2C] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Depuis 2018 sur le terrain</div>
          </div>
          <div className="fade-up order-1 lg:order-2 lg:col-span-6">
            <p className="mb-6 text-sm uppercase tracking-[0.3em] text-[#9A7B2C]">Coordination événementielle</p>
            <h1 className="font-serif text-4xl font-500 leading-[1.05] text-[#2C2C2C] sm:text-5xl lg:text-6xl">
              Anticiper, <span className="text-[#C9A84C]">orchestrer</span>, coordonner : l'expérience du terrain au service de vos événements.
            </h1>
            <div className="mt-9">
              <QuoteButton testId="coord-hero-quote" service="Coordination événementielle uniquement">Demander un devis coordination</QuoteButton>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#2C2C2C] py-24 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="reveal">
            <Eyebrow>Notre approche</Eyebrow>
            <p className="font-serif text-2xl leading-relaxed text-white lg:text-3xl">
              « Depuis 2018, l'expérience du terrain nourrit notre approche de l'événementiel : installation des buffets, organisation du service, coordination des équipes, gestion des flux, respect des horaires et adaptation aux imprévus. »
            </p>
            <p className="mt-6 text-base leading-relaxed text-white/70">
              La coordination peut être réservée <strong>indépendamment</strong> du traiteur Afro Regal, pour des missions ponctuelles comme pour un accompagnement complet.
            </p>
          </div>
        </div>
      </section>

      {/* Amalya */}
      <section className="bg-white py-24 lg:py-28" data-testid="amalya-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
          <div className="reveal order-2 overflow-hidden rounded-2xl lg:order-1">
            <img src={img("coordination_terrain")} alt="Amalya, coordination événementielle Afro Regal, sur le terrain lors d'une réception" className="h-full w-full object-cover" />
          </div>
          <div className="reveal order-1 lg:order-2">
            <Eyebrow>Sur le terrain</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-4xl">Amalya, la coordination au plus près de vos événements</h2>
            <p className="mt-5 text-base leading-relaxed text-[#2C2C2C]/70">
              Depuis 2018, Amalya intervient sur le terrain de l'événementiel : installation des buffets, organisation du service, coordination des équipes et des prestataires, gestion des flux et des imprévus. Cette expérience opérationnelle concrète nourrit une approche rigoureuse, à l'écoute et adaptée à chaque projet.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#2C2C2C]/70">
              Un interlocuteur unique, présent le jour J, pour que tout se déroule comme prévu — et que vous profitiez pleinement de votre événement.
            </p>
            <div className="mt-8">
              <QuoteButton testId="amalya-cta-quote" service="Coordination événementielle uniquement">Présenter mon projet</QuoteButton>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="reveal mb-14">
            <Eyebrow>Nos prestations</Eyebrow>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-4xl">Un cadre clair, une exécution maîtrisée</h2>
          </div>
          <ol className="relative border-l border-[#C9A84C]/50 pl-10">
            {PRESTATIONS.map((p, i) => (
              <li key={p} className="reveal relative pb-10 last:pb-0" style={{ transitionDelay: `${i * 40}ms` }} data-testid={`coord-prestation-${i}`}>
                <span className="absolute -left-[3.05rem] top-0 flex h-8 w-8 items-center justify-center rounded-full border border-[#C9A84C] bg-[#FAF6EF] text-xs font-medium text-[#9A7B2C]">{i + 1}</span>
                <p className="font-serif text-xl text-[#2C2C2C] lg:text-2xl">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <div className="reveal inline-flex items-start gap-3 rounded-xl border border-[#C9A84C]/40 bg-white p-5 text-left">
            <Globe size={20} className="mt-0.5 shrink-0 text-[#C9A84C]" />
            <p className="text-sm text-[#2C2C2C]/80">{zone}</p>
          </div>
          <div className="reveal mt-10 flex justify-center">
            <QuoteButton testId="coord-cta-quote" service="Coordination événementielle uniquement">Demander un devis coordination</QuoteButton>
          </div>
        </div>
      </section>
    </div>
  );
}
